<template>
  <article class="container-fluid bg">
    <div class="container-fixed">
      <div class="row offset-top-8 offset-bottom-8">
        <div class="col-xl-4 col-l-3 col-m-2 col-s-hide" />
        <div class="col-xl-4 col-l-5 col-m-8 col-s-12" style="padding: 24px; background: white">
          <header>
            <h1 class="text-center offset-top-1">
              {{ $t('common.headerLogin') }}
            </h1>
          </header>

          <p />

          <section>
            <VForm v-slot="{ isDisabled }" :form="form" disable-on-valid-submit @submit="onSubmit">
              <VNotification v-if="showExternalLoginNotification" id="login_information_tooltip" type="INFO" :content="'common.login_information_tooltip'" />

              <VInput name="username" :label="$t('common.username')" required :hint="$t('common.err_username_required')" />

              <VInput type="password" name="password" :label="$t('common.password')" required :hint="$t('common.err_password_required')" />

              <VSelect name="crm" :label="$t('common.crm')">
                <option v-for="eachCrm in crms" :key="eachCrm" :value="eachCrm">{{ $t('common.crm_' + eachCrm) }}</option>
              </VSelect>

              <VSelect v-model="$i18n.locale" name="language" :label="$t('common.language')">
                <option v-for="lang in languages" :key="lang" :value="lang">{{ $t('common.lang_' + lang) }}</option>
              </VSelect>

              <VCheckbox name="force" :label="$t('common.login_force_label')" :hint="$t('common.login_err_user_already_logged_in')" />

              <p id="internal_data_privacy" class="font-size-15" v-html="$t('common.internal_login_data_privacy')" />

              <template v-if="!isAddMode">
                <button type="submit" class="btn btn-brand h-ctr offset-top-2" :disabled="isDisabled" data-test="login">{{ $t('common.btnLogin') }}</button>
                <VSpinner v-if="isLoading" class="offset-top-2" style="padding-bottom: 0" />
                <!--<button v-else-if="isLoginTemplateAllowed" class="btn btn-icon btn-clean h-ctr offset-top-2" @click.prevent="isAddMode = true">
                  <span class="sr-only">+</span>
                  <span class="icon icon-add"></span>
                </button>-->
                <button v-else-if="isLoginTemplateAllowed" class="btn btn-icon btn-clean h-ctr offset-top-2" @click.prevent="isAddMode = true">
                  <span class="sr-only">+</span>
                  <VSvgIcon name="add" aria-hidden="true" />
                </button>
              </template>
              <template v-else>
                <hr />

                <VInput v-model="templateName" name="name" :label="$t('common.login_template_description')" />

                <VInput v-model="memberIdType" name="memberIdType" placeholder="FixedNetContractId" :label="$t('common.memberIdType')" />

                <VInput v-model="contractId" name="contractId" :label="$t('common.contractId')" />

                <div class="text-center offset-top-1">
                  <button class="btn btn-brand offset-top-1 offset-right-1" @click.prevent="onTemplateSave">{{ $t('common.save') }}</button>
                  <button class="btn btn-default offset-top-1 offset-right-1" @click.prevent="isAddMode = false">{{ $t('common.abort') }}</button>
                </div>
              </template>

              <p class="clearfix" />
            </VForm>
          </section>

          <section>
            <dl class="list-desc-grid list-desc-semibold">
              <dt>{{ $t('common.natco') }}:</dt>
              <dd>{{ natco }}</dd>

              <dt>{{ $t('common.environment') }}:</dt>
              <dd>{{ environment }}</dd>

              <dt>{{ $t('common.version') }}:</dt>
              <dd>{{ version }}</dd>
            </dl>
          </section>

          <section v-if="templates && isLoginTemplateAllowed">
            <VueGlobalEvents @keyup.ctrl.shift="onShortcut" />
            <div v-for="(template, index) in templates" :key="index" style="padding-bottom: 6px">
              <div class="btn-sectioned">
                <button
                  class="btn btn-default btn-small"
                  style="width: 90%; padding-right: 12px; padding-left: 12px; overflow: hidden; text-overflow: ellipsis"
                  :title="template.memberIdType + ' : ' + template.contractId + ' (' + $t('common.crm_' + template.crm) + ')'"
                  @click="onLoginAndContractSelect(template)"
                >
                  <strong>{{ template.name }}</strong>
                  {{ template.memberIdType }} : {{ template.contractId }} ({{ $t('common.crm_' + template.crm) }})
                </button>
                <button class="btn btn-default btn-small" style="width: 10%; padding-left: 0" @click="onDelete(template)">
                  <span class="sr-only">-</span>
                  <VSvgIcon name="cancel" variant="outlined" size="small" style="margin-left: 10px" />
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import Form from '@/components/Form/Form';
import Input from '@/components/Form/Input';
import Select from '@/components/Form/Select';
import VCheckbox from '@/components/Form/Checkbox';
import { focusFirst, getFormValues, initFields } from '@/utils/form';
import TitleMixin from '@/mixins/title';
import VSpinner from '@/components/Spinner';
import VNotification from '@/components/Notification';
import focus from '@/directives/focus';
import { CONTRACT_SEARCH, CONTRACT_SEARCH_REDIRECT, HOME } from '@route-types';
import VSvgIcon from '@/components/UI/VSvgIcon.vue';

export default {
  name: 'Login',

  directives: {
    focus,
  },

  components: {
    VSvgIcon,
    VueGlobalEvents: () => import('vue-global-events'),
    VForm: Form,
    VInput: Input,
    VSelect: Select,
    VSpinner,
    VCheckbox,
    VNotification,
  },

  mixins: [TitleMixin],

  props: {
    originalToName: {
      type: String,
      required: false,
      default: null,
    },

    originalToParams: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    const form = initFields({ name: 'username' }, { name: 'password' }, { name: 'crm' }, { name: 'force' });

    return {
      isAddMode: false,
      templateName: '',
      memberIdType: '',
      contractId: '',
      templates: [],
      form,
      isLoading: false,
      userAlreadyLoggedIn: false,
    };
  },

  computed: {
    languages() {
      if (this.availableLanguages !== null) return this.availableLanguages;
      return Object.keys(this.$i18n.messages);
    },

    crms() {
      if (this.availableCrms === null) return [];
      return this.availableCrms.filter((crm) => crm !== 'NoCRM');
    },

    version() {
      return process.env.VUE_APP_VERSION;
    },

    isLoginTemplateAllowed() {
      return !this.isProduction;
    },

    showExternalLoginNotification() {
      return !this.showPasswordChangeForm;
    },

    ...mapState('system', ['natco', 'environment', 'availableLanguages', 'availableCrms', 'sessionId']),

    ...mapGetters('system', ['isProduction', 'showPasswordChangeForm']),
  },

  created() {
    this.templates = JSON.parse(localStorage.getItem('templates'));
    if (!this.templates || !Array.isArray(this.templates)) {
      this.templates = [];
    }
    [this.form.crm.value] = this.crms || [];

    // always display login dialog in brand colors
    this.$store.dispatch('system/setWholesalePartnerId', null);
  },

  methods: {
    async onLoginAndContractSelect(template) {
      this.$i18n.locale = template.locale;
      await this.onSubmit({
        form: this.form,
        values: {
          username: template.username || this.form.username.value,
          password: template.password || this.form.password.value,
          crm: template.crm,
          force: this.form.force.value || true,
        },
      });
      this.$router.push({
        name: this.originalToName ? CONTRACT_SEARCH_REDIRECT : CONTRACT_SEARCH,
        params: {
          memberIdType: template.memberIdType,
          contractId: template.contractId,
          originalToName: this.originalToName,
          originalToParams: this.originalToParams,
        },
      });
    },

    onDelete(template) {
      this.templates = this.templates.filter(
        (t) => t.name !== template.name || t.contractId !== template.contractId || t.memberIdType !== template.memberIdType
      );
      localStorage.setItem('templates', JSON.stringify(this.templates));
    },

    onTemplateSave() {
      this.templates.push({
        ...getFormValues(this.form),
        name: this.templateName,
        locale: this.$i18n.locale,
        memberIdType: this.memberIdType,
        contractId: this.contractId,
      });
      localStorage.setItem('templates', JSON.stringify(this.templates));
      this.isAddMode = false;
    },

    async onSubmit({ values, form }) {
      form.isDisabled = true;
      this.isLoading = true;

      try {
        await this.$store.dispatch('system/login', {
          ...values,
          locale: this.$i18n.locale,
        });

        if (this.originalToName) {
          await this.$router.push({
            name: this.originalToName,
            params: this.originalToParams,
          });
        } else {
          await this.$router.push({
            name: HOME,
          });
        }
      } catch (error) {
        if (error.data?.code === 'USER_ALREADY_LOGGED_IN') {
          form.force.setInvalid('common.login_err_user_already_logged_in');
        } else {
          form.username.setInvalid('common.err_login_failed');
        }
        form.isDisabled = false;
        this.isLoading = false;
        this.$nextTick(() => {
          focusFirst(form);
        });
      }
    },

    onShortcut(event) {
      if (event.code.substr(0, 5) !== 'Digit') return;
      const index = Number.parseInt(event.code.substr(5), 10) - 1;
      if (index < 0) return;
      if (!this.templates[index]) return;
      this.onLoginAndContractSelect(this.templates[index]);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

@media screen and (min-width: #{$grid-medium-min}px) {
  .bg {
    height: 90vh;
    min-height: 500px;
    background-image: url('~@/assets/images/banners/login.jpg');
    background-repeat: no-repeat;
    background-position: right center;
    background-size: cover;
  }

  .list-desc-grid {
    dd {
      width: calc(50% - 6px);
    }

    dt {
      width: calc(50% - 6px);
    }
  }
}

.font-size-15 {
  font-size: 15px;
}
</style>
